/**
 * Traduccion entre el modelo de UI de la vista contable (ingles, `@/lib/types`)
 * y el modulo `ledger` del backend (espanol, `app/modules/ledger/router.py`).
 *
 * Mismo criterio que `routes/masters/mapping.ts`: NO es conversion de casing.
 * `JournalLine.debit` no es `debit`, es `debe`; `JournalEntry.book` no es un
 * campo de la fila sino el libro por el que se PREGUNTO, y el valor tampoco
 * coincide ("STATUTORY_PE" en la SPA, "statutory" en la base). Un conversor
 * automatico dejaria pasar las dos y el sintoma seria un diario vacio o —peor—
 * los numeros del libro equivocado, que se leen perfectamente plausibles.
 *
 * Las formas salen de EJECUTAR el servicio: `GET /openapi.json` mas un ciclo
 * real (postear un asiento -> diario -> mayor -> balance -> analisis) contra el
 * backend con `X-Tenant-Id`. Los `//: observado` marcan lo que solo se ve
 * corriendo, empezando por el mas caro: TODOS los importes viajan como STRING
 * ("1000.00"), tambien dentro de los totales del balance.
 */

import type {
  BookId,
  ClassificationSource,
  JournalEntry,
  JournalLine,
  LedgerAccount,
  LedgerMovement,
  TrialBalanceRow,
} from "@/lib/types"

/* ==========================================================================
 * La sociedad (`entity_id`)
 * ==========================================================================
 * En `masters` la sociedad solo hacia falta para ESCRIBIR. Aca hace falta para
 * LEER: `/ledger/journal`, `/trial-balance`, `/ledger/{cuenta}` y
 * `/analysis/{cuenta}` la declaran `Query(...)` obligatoria, porque un libro
 * pertenece a una sociedad y no al tenant. Sin sociedad no hay pantalla.
 *
 * La elige la persona en el selector de la cabecera y vive en la sesion, junto
 * al tenant (`lib/api.ts`, resuelta por `lib/activeEntity.ts`). Este modulo NO
 * guarda una copia: dos "sociedades activas" que se pueden desincronizar —los
 * maestros de una y los libros de otra, en la misma sesion— es exactamente el
 * bug que nadie encuentra. Se lee de la sesion en `books/api.ts`, que es donde
 * se arma la peticion.
 */

/* ==========================================================================
 * Libros: `BookId` (SPA)  <->  `book.tipo` (backend)
 * ==========================================================================
 * La SPA nombra el libro legal con el pais dentro ("STATUTORY_PE") porque el
 * producto es multipais y el libro estatutario CAMBIA de contenido segun la
 * jurisdiccion. El backend guarda el TIPO ("statutory") y el pais vive en la
 * entidad. Los otros dos coinciden salvo por las mayusculas.
 *
 * Al leer, cualquier tipo desconocido cae a "STATUTORY_PE": es el libro que
 * siempre existe, y equivocarse hacia el legal es el error conservador.
 * TODO(SPA): cuando haya entidades de otro pais, `STATUTORY_PE` deja de servir
 * como identificador y hay que derivar el pais de la entidad activa.
 */
const BOOK_A_TIPO: Record<BookId, string> = {
  STATUTORY_PE: "statutory",
  TAX: "tax",
  MANAGEMENT: "management",
}

const TIPO_A_BOOK: Record<string, BookId> = {
  statutory: "STATUTORY_PE",
  tax: "TAX",
  management: "MANAGEMENT",
}

export function bookToApi(book: BookId): string {
  return BOOK_A_TIPO[book] ?? "statutory"
}

export function bookFromApi(tipo: string | null | undefined): BookId {
  return TIPO_A_BOOK[tipo ?? ""] ?? "STATUTORY_PE"
}

/* ==========================================================================
 * Formas reales del backend (observadas, no deducidas)
 * ========================================================================== */

/** Una fila de `GET /ledger/journal`. OJO: NO trae las lineas (ver abajo). */
export interface JournalEntryRow {
  id: string
  fecha: string
  //: "AAAA-MM"
  periodo: string
  glosa: string
  //: automatico | manual | ajuste | reversion | cierre
  origen: string
  document_id: string | null
  treatment_rule_id: string | null
  pipeline_run_id: string | null
  asiento_reversion_de: string | null
  //: observado: SUM() de NUMERIC viaja como string ("1000.00")
  total_debe: string | number | null
  total_haber: string | number | null
  lineas: number
}

/** Cabecera de `GET /ledger/entries/{id}` — es `je.*`, la fila entera. */
export interface EntryHeaderRow {
  id: string
  tenant_id: string
  entity_id: string
  book_id: string
  periodo: string
  fecha: string
  glosa: string
  origen: string
  document_id: string | null
  pipeline_run_id: string | null
  treatment_rule_id: string | null
  asiento_reversion_de: string | null
  //: uuid de app_user, o null (el asiento del servidor e2e no tiene autor)
  creado_por: string | null
  //: TIMESTAMPTZ con offset: "2026-08-20T17:19:54.620664-05:00"
  creado_en: string
  //: lo agrega el JOIN con `book`; es el unico sitio donde el libro viene dentro
  libro: string
}

/** Linea de `GET /ledger/entries/{id}` — es `jl.*` mas el JOIN de la cuenta. */
export interface EntryLineRow {
  id: string
  journal_entry_id: string
  account_local_id: string | null
  account_group_id: string | null
  debe: string | number | null
  haber: string | number | null
  moneda: string | null
  tipo_cambio: string | number | null
  cost_center_id: string | null
  party_id: string | null
  glosa: string | null
  cuenta_codigo: string | null
  cuenta_nombre: string | null
}

export interface EntryDetailRow {
  asiento: EntryHeaderRow
  lineas: EntryLineRow[]
}

/** `GET /ledger/accounts` */
export interface AccountRow {
  codigo: string
  nombre: string
  //: 2 = rubro, 3 = divisionaria. Coincide con la longitud del codigo (PCGE)
  nivel: number
  elemento: string
  cuenta_grupo: string | null
  nombre_grupo: string | null
}

/** Un movimiento de `GET /ledger/ledger/{cuenta}`. */
export interface LedgerMovementRow {
  fecha: string
  asiento_id: string
  glosa: string
  origen: string
  debe: string | number | null
  haber: string | number | null
  glosa_linea: string | null
  document_id: string | null
  //: saldo corrido, calculado por el backend fila a fila
  saldo: string | number | null
}

export interface LedgerAccountRow {
  cuenta: string
  libro: string
  movimientos: LedgerMovementRow[]
  saldo_final: string | number | null
}

/** Una cuenta del `GET /ledger/trial-balance`. */
export interface TrialBalanceAccountRow {
  codigo: string
  nombre: string
  debe: string | number | null
  haber: string | number | null
  saldo: string | number | null
}

export interface TrialBalanceApiRow {
  libro: string
  corte: string
  cuentas: TrialBalanceAccountRow[]
  total_debe: string | number | null
  total_haber: string | number | null
  //: lo calcula el backend; no se recalcula aca (ver nota en trialBalanceFromApi)
  cuadra: boolean
  diferencia: string | number | null
}

/** `POST /ledger/entries` responde con el asiento creado, no con el detalle. */
export interface EntryCreatedRow {
  id: string
  libro: string
  total: string | number
}

/* ==========================================================================
 * Utilidades minimas (mismas que en masters/mapping.ts, a proposito)
 * ========================================================================== */

type Body = Record<string, unknown>

/** Los NUMERIC del backend llegan como string. Esto los normaliza sin mentir. */
function num(value: unknown, fallback = 0): number {
  if (value === null || value === undefined || value === "") return fallback
  const n = Number(value)
  return Number.isNaN(n) ? fallback : n
}

function isoDate(value: string | null | undefined): string {
  if (!value) return ""
  return value.length > 10 ? value.slice(0, 10) : value
}

/**
 * Rotulo corto y estable de un asiento.
 *
 * `journal_entry` NO tiene correlativo: su identidad es el uuid. La pantalla
 * necesita una columna "Asiento" legible, asi que se muestran los 8 primeros
 * caracteres del uuid REAL. No es un numero de asiento inventado — es el id,
 * recortado, y el id completo viaja igual en `JournalEntry.id` para el
 * drill-down. Se marca aqui porque un contador va a leer esa columna como si
 * fuera el correlativo del libro diario, y no lo es.
 * TODO(backend): `journal_entry.correlativo` por (entidad, libro, periodo), que
 * ademas hace falta para el PLE.
 */
function rotuloDeAsiento(id: string): string {
  return id.slice(0, 8)
}

/* ==========================================================================
 * Origen del asiento  —  `AsientoOrigen` (backend)  <->  `origin` (SPA)
 * ==========================================================================
 *   automatico <-> automatico
 *   manual     <-> manual
 *   ajuste     <-> ajuste
 *   cierre     <-> cierre
 *   reversion   -> ajuste
 *
 * El backend tiene un quinto origen, `reversion`, que la SPA no ofrece ni en su
 * tipo ni en el filtro de la barra. Se lee como "ajuste", que es lo que
 * operativamente es: una correccion posterior de un asiento ya posteado. La
 * alternativa —caer a "automatico"— diria que lo hizo el motor, y una reversion
 * la pide una persona.
 * TODO(SPA): agregar "reversion" a `JournalEntry["origin"]` y al filtro; hoy un
 * asiento de reversion se ve como ajuste y no se puede filtrar aparte.
 */
const ORIGENES: JournalEntry["origin"][] = ["automatico", "manual", "ajuste", "cierre"]

export function origenFromApi(origen: string | null | undefined): JournalEntry["origin"] {
  if (origen === "reversion") return "ajuste"
  return ORIGENES.includes(origen as JournalEntry["origin"])
    ? (origen as JournalEntry["origin"])
    : "automatico"
}

/* ==========================================================================
 * Libro diario  —  SPA `JournalEntry`  <->  `journal_entry` (+ `journal_line`)
 * ==========================================================================
 *   JournalEntry.id       <->  journal_entry.id
 *   JournalEntry.date     <->  journal_entry.fecha
 *   JournalEntry.glosa    <->  journal_entry.glosa
 *   JournalEntry.origin   <->  journal_entry.origen        (ver tabla arriba)
 *   JournalEntry.book     <->  book.tipo                   (por el que se pregunto)
 *   JournalEntry.number   <->  (no existe: prefijo del uuid, ver `rotuloDeAsiento`)
 *
 * LA ASIMETRIA IMPORTANTE: `GET /ledger/journal` NO devuelve las lineas. Hace
 * `GROUP BY je.id` y devuelve `total_debe`, `total_haber` y `lineas` (el conteo).
 * La SPA modela `JournalEntry.lines[]` y calculaba los totales sumandolas, asi
 * que con el backend real el diario se veria con todos los importes en cero.
 *
 * Decision: NO se pide el detalle de cada asiento (seria N+1 contra la base por
 * cada scroll del diario) y NO se fabrica una linea resumen —una linea falsa en
 * un libro diario es exactamente el tipo de dato que despues alguien cuadra—.
 * Se devuelve `lines: []` y los totales reales en campos aparte
 * (`JournalEntryView`), y la pantalla usa esos totales para la cabecera y no
 * dibuja lineas cuando no las tiene. `linesLoaded` dice cual de los dos casos es,
 * para que la pantalla no confunda "asiento sin lineas" con "lineas no traidas".
 * El detalle completo sigue disponible por `GET /ledger/entries/{id}`, que si las
 * trae, y es lo que abre el drill-down.
 * TODO(backend): `json_agg` de las lineas en `/ledger/journal`, como ya hace
 * `/masters/contracts` con `detalle`.
 *
 * SIN CONTRAPARTE en el backend:
 *   JournalEntry.createdBy — el SELECT del diario no trae `creado_por` (el del
 *     detalle si, pero es un uuid de `app_user`, no un nombre). Se lee "". La
 *     autoria real esta en `audit_log`, que es donde tiene que estar.
 *     TODO(backend): devolver el nombre del autor en el listado y en el detalle.
 *   JournalEntry.createdAt — idem; solo esta en el detalle (`creado_en`).
 *   JournalEntry.reason — el motivo de un asiento manual se guarda en
 *     `audit_log.motivo`, no en `journal_entry`. Se lee undefined: la pantalla
 *     deja de mostrar "motivo: ..." en vez de mostrar uno inventado.
 *     TODO(backend): exponer el motivo del audit_log junto al asiento.
 *   JournalEntry.status — `journal_entry` no tiene estado. Todo lo que devuelve
 *     el endpoint esta posteado (el motor no guarda borradores y no hay
 *     des-posteo: se corrige con un asiento de reversion). Se lee "posteado", que
 *     no es una aproximacion sino la unica verdad posible del modelo.
 *   JournalEntry.documentRef — el SELECT trae `document_id`, un uuid. La columna
 *     "Documento" de la pantalla espera una referencia legible ("F001-0004821").
 *     Un uuid ahi no es una referencia, es ruido: se lee undefined y la celda
 *     muestra "—".
 *     TODO(backend): `d.serie, d.numero` en el SELECT del diario.
 *
 * SIN CONTRAPARTE en la SPA:
 *   periodo, treatment_rule_id, pipeline_run_id, asiento_reversion_de  ->  se
 *   descartan al leer. `asiento_reversion_de` seria util (marcar "reversa el
 *   asiento X") pero `JournalEntry` no tiene donde ponerlo.
 */

/**
 * Lo que la pantalla del diario consume de verdad: un `JournalEntry` mas los
 * totales que el backend ya calculo. No se toca `lib/types.ts` porque estos
 * campos son de ESTA vista (el listado agregado), no del dominio: el asiento
 * completo, con sus lineas, sigue siendo `JournalEntry` a secas.
 */
export interface JournalEntryView extends JournalEntry {
  totalDebit: number
  totalCredit: number
  lineCount: number
  /** false = el listado no trae lineas; NO significa "asiento sin lineas". */
  linesLoaded: boolean
}

export function journalEntryFromApi(row: JournalEntryRow, ctx: { book: BookId }): JournalEntryView {
  return {
    id: row.id,
    number: rotuloDeAsiento(row.id),
    date: isoDate(row.fecha),
    book: ctx.book,
    glosa: row.glosa ?? "",
    documentRef: undefined,
    origin: origenFromApi(row.origen),
    createdBy: "",
    createdAt: "",
    reason: undefined,
    status: "posteado",
    lines: [],
    totalDebit: num(row.total_debe),
    totalCredit: num(row.total_haber),
    lineCount: num(row.lineas),
    linesLoaded: false,
  }
}

/* ==========================================================================
 * Detalle de asiento  —  SPA `JournalEntry`  <->  `GET /ledger/entries/{id}`
 * ==========================================================================
 *   JournalLine.accountCode <->  account_local.codigo   (JOIN, `cuenta_codigo`)
 *   JournalLine.accountName <->  account_local.nombre   (JOIN, `cuenta_nombre`)
 *   JournalLine.debit       <->  journal_line.debe
 *   JournalLine.credit      <->  journal_line.haber
 *   JournalLine.memo        <->  journal_line.glosa
 *   JournalEntry.book       <->  book.tipo              (aqui SI viene en la fila)
 *
 * Este endpoint es el unico que trae el libro dentro de la respuesta, asi que el
 * detalle no necesita contexto: se lee de `asiento.libro`.
 *
 * SIN CONTRAPARTE en la SPA:
 *   moneda, tipo_cambio, account_group_id, party_id  ->  se descartan al leer.
 *   `moneda` y `tipo_cambio` importan para una entidad multimoneda y `JournalLine`
 *   no los tiene; hoy todo el seed es PEN a tipo 1.000000.
 *   TODO(SPA): moneda por linea en `JournalLine` antes de la primera entidad que
 *   facture en USD.
 *
 * SIN CONTRAPARTE en el backend:
 *   JournalLine.costCenter — la linea trae `cost_center_id` (uuid) y la pantalla
 *     muestra el CODIGO. Sin un diccionario uuid -> codigo (el listado de centros
 *     de costo devuelve ambos, pero esta pantalla no lo carga) se lee undefined y
 *     la celda queda vacia, que es cierto.
 *     TODO(backend): `cc.codigo` en el SELECT de lineas, como ya hace con la cuenta.
 *   JournalEntry.createdBy — `creado_por` es un uuid de `app_user`. Mostrar un
 *     uuid donde dice "Autor" es peor que no mostrar nada: se lee "".
 */
export function entryFromApi(row: EntryDetailRow): JournalEntry {
  const cab = row.asiento
  return {
    id: cab.id,
    number: rotuloDeAsiento(cab.id),
    date: isoDate(cab.fecha),
    book: bookFromApi(cab.libro),
    glosa: cab.glosa ?? "",
    documentRef: undefined,
    origin: origenFromApi(cab.origen),
    createdBy: "",
    createdAt: cab.creado_en ?? "",
    reason: undefined,
    status: "posteado",
    lines: (row.lineas ?? []).map(lineFromApi),
  }
}

export function lineFromApi(row: EntryLineRow): JournalLine {
  return {
    id: row.id,
    accountCode: row.cuenta_codigo ?? "",
    accountName: row.cuenta_nombre ?? "",
    debit: num(row.debe),
    credit: num(row.haber),
    costCenter: undefined,
    memo: row.glosa ?? undefined,
  }
}

/* ==========================================================================
 * Plan de cuentas  —  `GET /ledger/accounts`
 * ==========================================================================
 *   code <->  account_local.codigo
 *   name <->  account_local.nombre
 *
 * Parece el mapeo trivial del modulo y es el que mas importa: el catalogo
 * sembrado (PCGE peruano) llega hasta la DIVISIONARIA de 3 digitos —"639"—, y
 * las listas fijas que traia la SPA usaban subdivisionarias de 5 —"63911"—, que
 * no existen en `account_local`. Con esas listas, TODO asiento manual y TODA
 * correccion de clasificacion morian en 422 contra el backend real. Por eso las
 * dos pantallas que eligen cuenta usan ahora este catalogo y no una constante.
 *
 * SIN CONTRAPARTE en la SPA: nivel, elemento, cuenta_grupo, nombre_grupo. La
 * correspondencia gerencial (`cuenta_grupo`) es informacion util para la
 * pantalla de analisis, pero `LedgerAccount` no tiene donde ponerla.
 */
export interface AccountOption {
  code: string
  name: string
}

export function accountFromApi(row: AccountRow): AccountOption {
  return { code: row.codigo, name: row.nombre }
}

/* ==========================================================================
 * Libro mayor  —  SPA `LedgerAccount`  <->  `GET /ledger/ledger/{cuenta}`
 * ==========================================================================
 *   LedgerMovement.date     <->  fecha
 *   LedgerMovement.glosa    <->  glosa (+ glosa_linea, ver abajo)
 *   LedgerMovement.debit    <->  debe
 *   LedgerMovement.credit   <->  haber
 *   LedgerMovement.balance  <->  saldo        (saldo corrido, lo da el backend)
 *   LedgerMovement.entryId  <->  asiento_id
 *   LedgerAccount.closingBalance <->  saldo_final
 *
 * `glosa`: el backend devuelve dos, la del asiento y la de la linea. La pantalla
 * tiene una sola columna. Se compone "glosa · glosa_linea" cuando la de la linea
 * existe y aporta algo distinto, que es lo mismo que ya hacian los mocks.
 *
 * `id` del movimiento: no existe. `journal_line.id` no viaja en esta respuesta
 * (el SELECT no lo pide), asi que se compone `asiento_id#indice`, estable
 * mientras el orden lo sea (el backend ordena por `je.fecha, je.id`). Solo se usa
 * como `key` de React.
 *
 * SIN CONTRAPARTE en el backend:
 *   LedgerAccount.openingBalance — el endpoint acepta `desde`, pero el saldo
 *     corrido SIEMPRE arranca en 0: no calcula el arrastre anterior al rango. Con
 *     `desde` puesto, el "saldo" de la primera fila no es el saldo de la cuenta.
 *     Se lee 0 y la pantalla muestra saldo inicial cero. Es lo unico honesto que
 *     se puede decir: cualquier otro numero seria inventado.
 *     TODO(backend): devolver `saldo_inicial` (suma de debe-haber anterior a
 *     `desde`) en `/ledger/ledger/{cuenta}`; sin eso el mayor de un rango que no
 *     empieza en el ejercicio no cuadra con el balance.
 *   LedgerMovement.entryNumber — mismo caso que en el diario: no hay correlativo.
 *     Se usa el prefijo del uuid del asiento.
 *   LedgerMovement.documentRef — `document_id` es un uuid, no una referencia.
 *     undefined.
 */
export function ledgerAccountFromApi(
  row: LedgerAccountRow,
  ctx?: { accountName?: string },
): LedgerAccount {
  return {
    accountCode: row.cuenta,
    accountName: ctx?.accountName ?? row.cuenta,
    openingBalance: 0,
    movements: (row.movimientos ?? []).map((m, i) => ledgerMovementFromApi(m, i)),
    closingBalance: num(row.saldo_final),
  }
}

export function ledgerMovementFromApi(row: LedgerMovementRow, index: number): LedgerMovement {
  const glosa = row.glosa ?? ""
  const glosaLinea = row.glosa_linea ?? ""
  return {
    id: `${row.asiento_id}#${index}`,
    date: isoDate(row.fecha),
    entryNumber: rotuloDeAsiento(row.asiento_id),
    glosa: glosaLinea && glosaLinea !== glosa ? `${glosa} · ${glosaLinea}` : glosa,
    documentRef: undefined,
    debit: num(row.debe),
    credit: num(row.haber),
    balance: num(row.saldo),
    entryId: row.asiento_id,
  }
}

/* ==========================================================================
 * Balance de comprobacion  —  SPA `TrialBalanceRow`  <->  `/ledger/trial-balance`
 * ==========================================================================
 *   accountCode  <->  codigo
 *   accountName  <->  nombre
 *   periodDebit  <->  debe
 *   periodCredit <->  haber
 *   closingDebit / closingCredit  <->  saldo, partido por signo
 *
 * LA ASIMETRIA: la SPA modela un balance de SEIS columnas (apertura, periodo,
 * cierre) y el backend devuelve TRES numeros por cuenta, acumulados desde el
 * principio hasta la fecha de corte — el endpoint solo acepta `hasta`, no
 * `desde`, asi que no existe la nocion de "movimientos del periodo" separada de
 * "acumulado".
 *
 * Decision: apertura = 0, periodo = lo acumulado hasta el corte, cierre =
 * apertura + periodo. Las tres columnas quedan matematicamente consistentes
 * (apertura + periodo = cierre) y ninguna miente: con apertura 0, "movimientos
 * del periodo" y "acumulado" son el mismo numero, que es literalmente lo que el
 * backend calculo. Lo que se pierde es poder leer el balance de UN mes; lo que
 * se muestra es el acumulado al corte, que es lo que el backend sabe.
 * TODO(backend): `desde` en `/ledger/trial-balance` y un `saldo_anterior` por
 * cuenta; sin eso no hay balance mensual comparativo.
 *
 * `level`: el backend no lo devuelve aqui (si en `/ledger/accounts`). Se usa la
 * longitud del codigo, que en el PCGE ES el nivel: verificado contra el catalogo
 * real, las 82 cuentas de `nivel` 2 tienen codigo de 2 caracteres y las 323 de
 * `nivel` 3, de 3. No es una heuristica: es como esta construido el plan.
 *
 * `cuadra` viene calculado por el backend y NO se recalcula. Es deliberado: si el
 * balance no cuadra, la pantalla tiene que gritar lo mismo que dice el servidor,
 * no una segunda opinion del cliente redondeando floats. Un descuadre de 0.004
 * que el backend ve y la SPA no es el peor de los dos mundos.
 */
export interface TrialBalanceResult {
  rows: TrialBalanceRow[]
  totals: {
    openingDebit: number
    openingCredit: number
    periodDebit: number
    periodCredit: number
    closingDebit: number
    closingCredit: number
  }
  balanced: boolean
}

export function trialBalanceRowFromApi(row: TrialBalanceAccountRow): TrialBalanceRow {
  const debe = num(row.debe)
  const haber = num(row.haber)
  const saldo = num(row.saldo)
  return {
    accountCode: row.codigo,
    accountName: row.nombre ?? "",
    level: row.codigo?.length ?? 0,
    openingDebit: 0,
    openingCredit: 0,
    periodDebit: debe,
    periodCredit: haber,
    closingDebit: saldo > 0 ? saldo : 0,
    closingCredit: saldo < 0 ? -saldo : 0,
  }
}

export function trialBalanceFromApi(row: TrialBalanceApiRow): TrialBalanceResult {
  const rows = (row.cuentas ?? []).map(trialBalanceRowFromApi)
  const totals = rows.reduce(
    (acc, r) => ({
      openingDebit: acc.openingDebit + r.openingDebit,
      openingCredit: acc.openingCredit + r.openingCredit,
      periodDebit: acc.periodDebit + r.periodDebit,
      periodCredit: acc.periodCredit + r.periodCredit,
      closingDebit: acc.closingDebit + r.closingDebit,
      closingCredit: acc.closingCredit + r.closingCredit,
    }),
    {
      openingDebit: 0,
      openingCredit: 0,
      periodDebit: 0,
      periodCredit: 0,
      closingDebit: 0,
      closingCredit: 0,
    },
  )
  // Los totales de la pantalla se recalculan desde las filas para que la suma
  // que se ve sea la suma de lo que se ve; el VEREDICTO (`cuadra`) es el del
  // backend, que suma con Decimal y no con float.
  return { rows, totals, balanced: Boolean(row.cuadra) }
}

/* ==========================================================================
 * Asiento manual  —  SPA `AdjustmentEntryPayload`  <->  `AsientoManualIn`
 * ==========================================================================
 *   book    <->  libro         (BookId -> book.tipo, ver arriba)
 *   date    <->  fecha
 *   glosa   <->  glosa         (min_length 3)
 *   reason  <->  motivo        (min_length 3, obligatorio)
 *   lines[] <->  lineas[]      (minimo 2), y dentro:
 *       accountCode  <->  cuenta   (el CODIGO local; el backend lo resuelve)
 *       debit        <->  debe
 *       credit       <->  haber
 *       —             ->  glosa    (se manda la glosa de la linea si la hay)
 *   —        ->  entity_id     (obligatorio; sale de la entidad activa)
 *
 * `lineas` es `List[Dict[str, Any]]` en Pydantic: el backend NO valida las
 * claves de cada linea, las lee con `.get()` en `postear()`. O sea que mandar
 * `accountCode` en vez de `cuenta` NO da 422: da un `ErrorAsiento` ("cuenta
 * desconocida: None") o, peor, una linea en cero. Es el mapeo mas silencioso del
 * modulo y por eso tiene test propio.
 *
 * SIN CONTRAPARTE en el backend:
 *   line.costCenter — `postear()` construye `Linea(cuenta, debe, haber, glosa)` y
 *     no acepta centro de costo; la clave se descartaria en silencio. NO se
 *     manda. La pantalla sigue mostrando el campo porque el dia que el backend lo
 *     acepte no hay que rehacer el formulario, pero hoy no viaja y esta dicho
 *     aqui y en el propio dialogo.
 *     TODO(backend): `cost_center_id` (o el codigo) en las lineas de
 *     `POST /ledger/entries`.
 *
 * `origen` no se manda: el backend fuerza "ajuste" para todo asiento de esta
 * ruta, que es correcto — un asiento hecho a mano desde la SPA es un ajuste.
 */
export interface AdjustmentEntryData {
  book: BookId
  date: string
  glosa: string
  lines: { accountCode: string; debit: number; credit: number; costCenter?: string }[]
  reason: string
}

export function adjustmentEntryToApi(data: AdjustmentEntryData, entityId: string): Body {
  return {
    entity_id: entityId,
    libro: bookToApi(data.book),
    fecha: data.date,
    glosa: data.glosa,
    motivo: data.reason,
    lineas: data.lines.map((l) => ({
      cuenta: l.accountCode,
      debe: l.debit,
      haber: l.credit,
    })),
  }
}

export function entryCreatedFromApi(row: EntryCreatedRow): {
  id: string
  book: BookId
  total: number
} {
  return { id: row.id, book: bookFromApi(row.libro), total: num(row.total) }
}
